import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { TextFormField } from 'lib/form/TextFormField';
import { CheckCircle } from 'ui/CheckCircle';
import { ButtonV2 } from 'ui/ButtonV2';
import { PayBaseModal } from './PayBaseModal';

const PREFIX = 'PayEmailReceiptModal';

const classes = {
    title: `${PREFIX}title`,
    button: `${PREFIX}button`,
    img: `${PREFIX}img`,
    successWrapper: `${PREFIX}successWrapper`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.title}`]: {
        fontSize: '16px',
        fontWeight: 400,
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(1)
    },

    [`& .${classes.button}`]: {
        marginTop: theme.spacing(2)
    },

    [`& .${classes.img}`]: {
        margin: theme.spacing(2, 'auto')
    },

    [`& .${classes.successWrapper}`]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

interface PayEmailReceiptModalProps {
    open: boolean;
    onClose: () => void;
    defaultEmail?: string;
    onSubmit: (data: { email: string }) => void;
    loading?: boolean;
    sent?: boolean;
}

export const PayEmailReceiptModal: React.FC<React.PropsWithChildren<PayEmailReceiptModalProps>> = ({
    open,
    onClose,
    defaultEmail,
    onSubmit,
    loading,
    sent
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const schema = React.useMemo(
        () =>
            Yup.object<{ userEmail: string }>().shape({
                email: Yup.string()
                    .required(t('FORMS_VALIDATION_REQUIRED'))
                    .email(t('FORMS_VALIDATION_EMAIL'))
            }),
        [t]
    );
    if (!open) {
        return null;
    }
    return (
        <PayBaseModal open={open} onClose={onClose}>
            <Formik
                onSubmit={onSubmit}
                initialValues={{ email: defaultEmail ?? '' }}
                validationSchema={schema}
            >
                {({ submitForm, isValid }) => (
                    <Form>
                        <StyledBox px={1} py={1.5}>
                            {!sent && (
                                <React.Fragment>
                                    <Typography align="center" className={classes.title}>
                                        {t('PAT_QUICKPAY_EMAIL_RECEIPT_CONTENT')}
                                    </Typography>
                                    <TextFormField disabled={loading} name="email" type="email" />
                                    <ButtonV2
                                        className={classes.button}
                                        disabled={loading || !isValid}
                                        onClick={submitForm}
                                    >
                                        {t('BUTTON_SUBMIT')}
                                    </ButtonV2>
                                </React.Fragment>
                            )}
                            {sent && (
                                <Box className={classes.successWrapper}>
                                    <CheckCircle color={theme.palette.primary.main} className={classes.img} />
                                    <Typography align="center" className={classes.title}>
                                        {t('ACTIONS_RESEND_RECEIPT_SUCCESS')}
                                    </Typography>
                                </Box>
                            )}
                        </StyledBox>
                    </Form>
                )}
            </Formik>
        </PayBaseModal>
    );
};
